import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { Row, Col, Button, Select, Switch, Modal, InputNumber, Divider, DatePicker } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import i from '../../translations/i'
import { Titles } from '../../layouts/Titles'
import controller from '../settings/project/project.controller'
import { storeLocal } from '../../helpers/stores'
import { ErrorByPricingPlan } from '../../components/ErrorByPricingPlan'
import { TextEditor } from "../../helpers/TextEditor"
import { dayToUtcTimestamp, utcTimestampToDay } from "../../helpers/timestamp/datetime.helper"
import { SwitchBlock } from "../../components/SwitchBlock";
import PaymentTypeController from "../settings/payment/payment.type.controller";
import OptionTypesController from "../../components/options/optionTypes.controller";
import OptionValuesController from "../../components/options/optionValues.controller";

export const BonusProgram = observer(() => {

    useEffect(() => {
        controller.get()
        PaymentTypeController.getList()
        OptionTypesController.get([], ['bonusTopupEnabled', 'bonusTopupPaymentTypeId', 'bonusTopupAmounts'])
            .then((keys: string[]) => OptionValuesController.get(keys))
    }, [])

    const buttons = [
        { title: i.t('def:actions.save'), htmlType: `submit`, type: 'primary' }
    ]

    const getOptionValue = (key: string, field: string, defaultValue: any = null) => {
        const option = OptionValuesController.items[key]

        if(option && typeof option === 'object') {
            return option[field] ?? defaultValue
        }

        return option ?? defaultValue
    }

    const bonusTopupEnabled = !!Number(getOptionValue('bonusTopupEnabled', 'value_int', 0))
    const bonusTopupPaymentTypeId = getOptionValue('bonusTopupPaymentTypeId', 'value_int')
    const bonusTopupAmounts = (() => {
        const value = getOptionValue('bonusTopupAmounts', 'value_text', '[]')

        if(Array.isArray(value))
            return value

        try {
            const amounts = JSON.parse(value || '[]')
            return Array.isArray(amounts) ? amounts : []
        } catch {
            return []
        }
    })()

    const setBonusTopupAmounts = (amounts: number[]) =>
        OptionValuesController.onChangeValue('bonusTopupAmounts', JSON.stringify(amounts))

    return controller.isset ? (
        <>
            <form onSubmit={(e) => {
                controller.save(e)
                OptionValuesController.save(e)
            }}>
                <Titles
                    title={i.t('def:bonus.title')}
                    buttons={buttons}
                />

                <Row gutter={[20, 20]}>
                    <Col span={24}>
                        <label>{i.t('def:bonus.description')}</label>

                        <TextEditor
                            content={controller.project.bonus_description ?? ''}
                            returned={(e: any) => controller.project.bonus_description = e}
                        />
                    </Col>
                </Row>

                <Divider />

                <Row gutter={[20, 20]}>
                    <Col span={24}>
                        <h3>{i.t('bonusProgram:titles.bonusCard')}</h3>
                    </Col>
                    <Col span={24}>
                        <SwitchBlock array={[
                            { label: i.t('def:bonus.bonus_card'), click: (e: any) => controller.project.bonus_card = e, value: controller.project.bonus_card },
                            { label: i.t('bonusProgram:showPoints'), click: (e: any) => controller.project.view_bonus_points = e, value: controller.project.view_bonus_points },
                        ]} />
                    </Col>
                    {controller.project.bonus_card
                        ? <>
                            <Col span={12}>
                                <label>{i.t('def:bonus.bonus_card_type')}</label>
                                <Select
                                    value={controller.project.bonus_card_type}
                                    options={[
                                        { label: "QRCode", value: "qrcode" },
                                        { label: "Barcode", value: "barcode" },
                                    ]}
                                    onChange={(e: string) => controller.set('bonus_card_type', e)}
                                    style={{ width: `100%` }}
                                />
                            </Col>
                            <Col span={12}>
                                <label>{i.t('def:bonus.bonus_card_value')}</label>
                                <Select
                                    value={controller.project.bonus_card_value}
                                    options={[
                                        { label: i.t('def:bonus.card.user_phone'), value: "user.phone" },
                                        { label: i.t('def:bonus.card.user_external_id'), value: "user.external_id" },
                                    ]}
                                    onChange={(e: string) => controller.set('bonus_card_value', e)}
                                    style={{ width: `100%` }}
                                />
                            </Col>
                        </>
                        : <></>
                    }
                </Row>

                <Divider />

                <Row gutter={[20, 20]}>
                    <Col span={24}>
                        <h3>{i.t('bonusProgram:topup.title')}</h3>
                    </Col>
                    <Col span={12}>
                        <SwitchBlock array={[
                            {
                                label: i.t('bonusProgram:topup.enabled'),
                                click: (e: any) => OptionValuesController.onChangeValue('bonusTopupEnabled', e),
                                value: bonusTopupEnabled,
                            },
                        ]} />
                    </Col>
                    {bonusTopupEnabled ? (
                        <>
                            <Col span={12}>
                                <label>{i.t('bonusProgram:topup.paymentType')}</label>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder={i.t('bonusProgram:topup.selectPaymentType')}
                                    value={bonusTopupPaymentTypeId || undefined}
                                    options={PaymentTypeController.list
                                        .filter((el: any) => el.enabled && el.online && el.payment_gateway_id)
                                        .map((el: any) => ({
                                            label: el.label,
                                            value: el.id,
                                        }))}
                                    onChange={(e: number) => OptionValuesController.onChangeValue('bonusTopupPaymentTypeId', e)}
                                />
                            </Col>
                            <Col span={24}>
                                <Row gutter={[20, 20]}>
                                    <Col span={24}>
                                        <label className="formLabel">{i.t('bonusProgram:topup.amounts')}</label>
                                    </Col>
                                    <Col span={24}>
                                        <Button onClick={() => setBonusTopupAmounts([...bonusTopupAmounts, 0])}>
                                            {i.t('def:actions.add')}
                                        </Button>
                                    </Col>
                                    {bonusTopupAmounts.map((amount: number, index: number) => (
                                        <Col span={24} key={index}>
                                            <Row gutter={[20, 20]} justify="space-between" align="bottom">
                                                <Col span={20}>
                                                    <label>{i.t('bonusProgram:topup.enterAmount')}</label>
                                                    <InputNumber
                                                        min={1}
                                                        placeholder={i.t('bonusProgram:topup.enterAmount')}
                                                        value={amount}
                                                        onChange={(value) => {
                                                            const amounts = [...bonusTopupAmounts]
                                                            amounts[index] = Number(value ?? 0)
                                                            setBonusTopupAmounts(amounts)
                                                        }}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Col>
                                                <Col span={4}>
                                                    <Button
                                                        icon={<DeleteOutlined />}
                                                        shape="circle"
                                                        type="dashed"
                                                        onClick={() => setBonusTopupAmounts(
                                                            bonusTopupAmounts.filter((_: number, amountIndex: number) => amountIndex !== index)
                                                        )}
                                                        danger
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </>
                    ) : null}
                </Row>

                <Divider />

                <Row gutter={[20, 20]}>
                    <Col span={24}>
                        <h3>{i.t('bonusProgram:titles.bonusProgram')}</h3>
                    </Col>
                    <Col span={24}>
                        <SwitchBlock array={[
                            { label: i.t('def:bonus.enabled'), click: (e: any) => controller.project.bonus = e, value: controller.project.bonus, block: 1 },
                            { label: i.t('bonusProgram:noWriteOffOldPrice'), click: (e: any) => controller.project.no_write_points_old_price = e, value: controller.project.no_write_points_old_price, block: 3 },
                            { label: i.t('bonusProgram:showAccruedPointsInList'), click: (e: any) => controller.project.visible_points_list_products = e, value: controller.project.visible_points_list_products, block: 1 },
                        ]} />
                    </Col>
                    {controller.project.bonus ?
                        <>
                            <Col span={12}>
                                <SwitchBlock array={[
                                    {
                                        label: i.t('def:bonus.hello.hello_switch'),
                                        click: (e: any) => controller.project.welcome_bonus = e,
                                        value: controller.project.welcome_bonus,
                                        block: 1
                                    },
                                ]} />
                            </Col>
                            <Col span={12}>
                                <label htmlFor="">{i.t('def:bonus.hello.hello_count')}</label>
                                <InputNumber
                                    value={controller.project.welcome_bonus_size}
                                    onChange={(e: any) => controller.project.welcome_bonus_size = e}
                                    style={{ width: `100%` }}
                                />
                            </Col>
                        </> : <></>
                    }
                </Row>

                <Divider />

                <Row gutter={[20, 20]}>
                    <Col span={24}>
                        <h3>{i.t('def:bonus.combustion.description')}</h3>
                    </Col>
                    <Col span={12}>
                        <SwitchBlock array={[
                            { label: i.t('def:bonus.combustion.combustion_switch'), click: (e: any) => controller.project.bonuses_expire = e, value: controller.project.bonuses_expire },
                        ]} />
                    </Col>
                    <Col span={12}>
                        <label>{i.t('def:bonus.combustion.combustion_date')}</label>
                        <InputNumber
                            style={{ width: '100%' }}
                            onChange={(e) => controller.set('combustion_days', e)}
                            value={controller.project.combustion_days}
                        />
                    </Col>
                </Row>

                <Divider />

                <Row gutter={[20, 20]}>
                    <Col span={24}>
                        <h3>{i.t('def:bonus.cart_percent')}</h3>
                    </Col>
                    <Col span={24}>
                        <SwitchBlock array={[
                            { label: i.t('def:bonus_accrue_upon_write_off'), click: (e: any) => controller.project.bonus_accrue_upon_write_off = e, value: controller.project.bonus_accrue_upon_write_off, block: 3 },
                            { label: i.t('def:bonus_charge_old_price'), click: (e: any) => controller.project.bonus_charge_old_price = e, value: controller.project.bonus_charge_old_price, block: 3 },
                            { label: i.t('bonusProgram:writeOffReducesMinOrder'), click: (e: any) => controller.project.bonus_writeoff_reduces_min_order = e, value: controller.project.bonus_writeoff_reduces_min_order, block: 1 },
                        ]} />
                    </Col>
                    <Col span={12}>
                        <label>{i.t('def:bonus.cart_percent_count')}</label>
                        <InputNumber
                            style={{ width: '100%' }}
                            onChange={(e) => controller.set('percent_bonus_cart', e)}
                            value={controller.project.percent_bonus_cart}
                        />
                    </Col>
                    <Col span={12}>
                        <label>{i.t('def:bonus.cashback_percent')}</label>
                        <InputNumber
                            style={{ width: '100%' }}
                            onChange={(e) => controller.set('cashback_percent', e)}
                            value={controller.project.cashback_percent}
                        />
                    </Col>
                </Row>

                <Divider />

                <Row gutter={[20, 20]}>
                    <Col span={24}>
                        <Button type="dashed" danger onClick={() => controller.openBonusMassAssignment()}>{i.t('def:Mass Assignment')}</Button>
                    </Col>
                </Row>

                <Modal
                    title={i.t('def:Mass Assignment')}
                    open={controller.bonusMassAssignment}
                    onOk={() => controller.saveBonusMassAssignment()}
                    onCancel={() => controller.closeBonusMassAssignment()}
                >
                    <Row gutter={[20, 20]}>
                        <Col span={24}>
                            <InputNumber
                                style={{ width: '100%' }}
                                value={controller.bonusMassAssignmentData.bonuses_add}
                                onChange={(e) => controller.bonusMassAssignmentData.bonuses_add = e ? e : 1}
                                min={1}
                                required
                            />
                        </Col>
                        <Col span={24}>
                            <DatePicker
                                style={{ width: '100%' }}
                                value={controller.bonusMassAssignmentData.bonuses_add_combustion_date ? utcTimestampToDay(controller.bonusMassAssignmentData.bonuses_add_combustion_date) : null}
                                placeholder={i.t('def:combustion_date')}
                                onChange={e => controller.bonusMassAssignmentData.bonuses_add_combustion_date = e ? dayToUtcTimestamp(e) : null}
                            />
                        </Col>
                    </Row>
                </Modal>
            </form>
        </>
    ) : <></>
})